import { Link } from "react-router-dom";
import { ArrowRight, Building2, Briefcase, Users2, UserCheck, Check, ShieldCheck, ClipboardCheck, Link2 } from "lucide-react";
import { GREEN, BLUE, ORANGE, SectionTitle } from "./shared";

export function PercorsoSection() {
  return (
    <section className="py-20 lg:py-24" data-testid="section-percorso">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="lp-reveal">
          <SectionTitle kicker="Come funziona" title="Da zero al tuo dossier ESG." />
        </div>
        <ol className="mt-12 grid md:grid-cols-5 gap-4 list-none p-0">
          {[
            "Crea l'azienda",
            "Rispondi alle domande semplici",
            "Collega JOBTIMEHR e importa i dati disponibili",
            "Completa ciò che manca",
            "Genera VSME e dossier banca",
          ].map((step, i) => (
            <li key={step} className="lp-reveal relative rounded-xl border border-slate-200 bg-white p-5"
              data-testid={`step-${i + 1}`}>
              <span className="inline-flex w-8 h-8 rounded-full items-center justify-center text-sm font-extrabold text-white"
                style={{ background: i === 4 ? ORANGE : GREEN, fontFamily: "Manrope, sans-serif" }}>
                {i + 1}
              </span>
              <p className="mt-3 text-sm font-semibold text-slate-800 leading-snug">{step}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function DifferenziatoreSection() {
  return (
    <section className="py-20 lg:py-24" style={{ background: "#FAFBFA" }} data-testid="section-differenziatore">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="lp-reveal text-center max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Non un questionario. <span style={{ color: GREEN }}>Un sistema ESG.</span>
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div className="lp-reveal rounded-2xl border border-slate-200 bg-white p-7">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Questionario tradizionale</p>
            <ul className="space-y-3">
              {["Inserisci dati", "Invia", "Fine"].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-sm text-slate-500">
                  <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-400">·</span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="lp-reveal rounded-2xl border-2 p-7 bg-white" style={{ borderColor: GREEN }}>
            <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: GREEN }}>ESG-PMI</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3">
              {["Raccoglie", "Riutilizza", "Calcola", "Verifica", "Aggiorna", "Confronta",
                "Prepara report", "Dialoga con JOBTIMEHR", "Ti assiste con Eva"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Check className="w-4 h-4 shrink-0" style={{ color: GREEN }} aria-hidden="true" />{t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PerChiSection() {
  return (
    <section id="per-chi" className="py-20 lg:py-24 scroll-mt-20" data-testid="section-per-chi">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="lp-reveal">
          <SectionTitle kicker="Per chi è" title="Costruita intorno alle PMI." />
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Building2, t: "Microimprese", d: "Percorso semplice e proporzionato alla dimensione." },
            { icon: Briefcase, t: "PMI", d: "Gestione strutturata dei dati ESG, VSME e dossier banca." },
            { icon: Users2, t: "Consulenti", d: "Gestione di più aziende clienti da un unico accesso." },
            { icon: UserCheck, t: "Commercialisti e consulenti del lavoro", d: "Un unico ambiente per accompagnare i clienti nel percorso ESG." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="lp-reveal rounded-xl border border-slate-200 bg-white p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <Icon className="w-6 h-6" style={{ color: GREEN }} aria-hidden="true" />
              <h3 className="mt-3 text-base font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>{t}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <div className="lp-reveal mt-10 rounded-2xl border border-slate-200 bg-white p-7 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-xl font-extrabold" style={{ fontFamily: "Manrope, sans-serif" }}>
              Un solo accesso. Tutte le aziende clienti.
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              La vista consulente aggrega lo stato ESG di tutte le aziende gestite: completamento,
              dati mancanti e priorità di lavoro.
            </p>
          </div>
          <div className="grid grid-cols-4 gap-3" data-testid="consulenti-mockup" role="img"
            aria-label="Esempio di dashboard consulente — dati dimostrativi: 48 aziende, 31 VSME completi, 12 in lavorazione, 5 da avviare">
            {[
              ["48", "aziende", GREEN], ["31", "VSME completi", BLUE],
              ["12", "in lavorazione", "#B85E0F"], ["5", "da avviare", "#64748B"],
            ].map(([n, l, c]) => (
              <div key={l} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
                <p className="text-2xl font-extrabold" style={{ color: c, fontFamily: "Manrope, sans-serif" }}>{n}</p>
                <p className="text-[10px] font-medium text-slate-600 mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          <p className="col-span-full text-[11px] text-slate-400 italic mt-2">Esempio di dashboard — dati dimostrativi.</p>
        </div>
        <div className="mt-8 flex flex-wrap gap-3" data-testid="per-chi-ctas">
          <Link to="/pmi"
            className="inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-md text-white transition-transform hover:-translate-y-0.5"
            style={{ background: GREEN }} data-testid="cta-pmi">
            Per le PMI <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <Link to="/consulenti"
            className="inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-md border-2 transition-colors"
            style={{ borderColor: BLUE, color: BLUE }} data-testid="cta-consulenti">
            Per i consulenti <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function TrasparenzaSection() {
  return (
    <section className="py-20 lg:py-24" style={{ background: "#FAFBFA" }} data-testid="section-trasparenza">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="lp-reveal">
          <SectionTitle
            kicker="Dati e trasparenza"
            title="Ogni numero deve poter essere spiegato."
            sub="ESG-PMI conserva la provenienza del dato e la metodologia utilizzata: chi l'ha inserito, quando, con quale fonte e quale formula." />
        </div>
        <div className="lp-reveal rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="trasparenza-example">
          <div className="rounded-lg p-4 mb-3" style={{ background: "#E7F4EE" }}>
            <div className="flex items-baseline justify-between">
              <p className="text-sm font-bold" style={{ color: GREEN }}>Scope 2</p>
              <p className="text-xl font-extrabold" style={{ color: GREEN, fontFamily: "Manrope, sans-serif" }}>18,7 tCO₂e</p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              ["Consumo", "48.500 kWh energia elettrica"],
              ["Fattore", "0,385 kgCO₂e/kWh · fattore medio indicativo"],
              ["Fonte", "Bollette + importazione"],
              ["Formula", "attività × fattore di emissione"],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-3 text-sm rounded-lg bg-slate-50 border border-slate-100 px-3.5 py-2.5">
                <span className="w-16 shrink-0 text-xs font-bold uppercase tracking-wide text-slate-500 pt-0.5">{k}</span>
                <span className="text-slate-800 font-medium">{v}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-slate-400 italic">
            Esempio dimostrativo. Il fattore effettivo dipende da anno, fonte, metodologia e perimetro di calcolo.
          </p>
        </div>
      </div>
    </section>
  );
}

export function SicurezzaSection() {
  return (
    <section className="py-20 lg:py-24" data-testid="section-sicurezza">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="lp-reveal rounded-2xl border border-slate-200 bg-white p-7">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              [ShieldCheck, "Separazione tra aziende", "ogni organizzazione vede solo i propri dati"],
              [UserCheck, "Permessi per ruolo", "inserimento, verifica e approvazione separati"],
              [ClipboardCheck, "Audit trail", "ogni modifica ai dati è tracciata"],
              [Link2, "Integrazione JOBTIMEHR controllata", "sincronizzazione con anteprima e gestione conflitti"],
            ].map(([Icon, t, d]) => (
              <div key={t} className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                <Icon className="w-5 h-5" style={{ color: BLUE }} aria-hidden="true" />
                <p className="mt-2 text-sm font-bold text-slate-900">{t}</p>
                <p className="text-xs text-slate-600 mt-1">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-reveal">
          <SectionTitle
            kicker="Sicurezza"
            title="I tuoi dati restano i tuoi dati."
            sub="Separazione tra aziende, permessi per ruolo, tracciamento delle modifiche e minimizzazione dei dati: raccogliamo solo ciò che serve alla rendicontazione." />
        </div>
      </div>
    </section>
  );
}
